import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  Tabs,
  Tab,
  Skeleton,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Chip,
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import FeedLayout from '../../components/feed/FeedLayout';
import { getRegions, getLocations } from '../../services/feedApi';

const regionTabs = [
  { id: 'pacific', name: 'Pacific', apiName: 'Pacific' },
  { id: 'mountain-west', name: 'Mountain West', apiName: 'Mountain West' },
  { id: 'midwest', name: 'Midwest', apiName: 'Midwest' },
  { id: 'gulf-coast', name: 'Gulf Coast', apiName: 'Gulf Coast' },
  { id: 'atlantic-north', name: 'Atlantic North', apiName: 'Atlantic North' },
  { id: 'atlantic-south', name: 'Atlantic South', apiName: 'Atlantic South' },
  { id: 'southeast', name: 'Southeast', apiName: 'Southeast' },
  { id: 'idaho', name: 'Idaho', apiName: 'Idaho' },
  { id: 'insights', name: 'Regional Insights' },
];


const LocationRow = ({ location, onClick }) => (
  <ListItem
    button
    onClick={onClick}
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      mb: 2,
      bgcolor: 'white',
      '&:hover': { 
        bgcolor: 'rgba(59, 89, 152, 0.04)',
        borderColor: '#3b5998',
      },
    }}
  >
    <ListItemText
      primary={
        <Box>
          <Typography variant="caption" color="text.secondary">
            {location.storeCode}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1a2b4a' }}>
            {location.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {location.address}
          </Typography>
        </Box>
      }
    />
    <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      {location.rating && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <StarIcon sx={{ color: '#f59e0b', fontSize: 18 }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {location.rating}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ({location.reviewCount})
          </Typography>
        </Box>
      )}
      <IconButton edge="end" onClick={onClick}>
        <ChevronRightIcon />
      </IconButton>
    </ListItemSecondaryAction>
  </ListItem>
);

const RegionalInsights = ({ regions }) => (
  <Box>
    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
      Regional Performance Insights
    </Typography>
    <Grid container spacing={3}>
      {regions.map((region) => (
        <Grid item xs={12} sm={6} md={4} key={region.name}>
          <Card sx={{ border: '1px solid', borderColor: 'divider', p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1a2b4a', mb: 2 }}>
              {region.name}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Locations</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{region.locationCount}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Avg Rating</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {Number.isFinite(region.avgRating) ? region.avgRating : '—'}
                </Typography>
                {Number.isFinite(region.avgRating) && (
                  <StarIcon sx={{ color: '#f59e0b', fontSize: 16 }} />
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Total Reviews</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{region.totalReviews ?? 0}</Typography>
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
  </Box>
);

const FeedRegions = () => {
  const navigate = useNavigate();
  const { regionId } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [sortBy, setSortBy] = useState('storeCode');
  const [regions, setRegions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const tabIndex = regionTabs.findIndex((tab) => tab.id === regionId);
    if (tabIndex >= 0) {
      setActiveTab(tabIndex);
    }
  }, [regionId]);

  useEffect(() => {
    const currentTab = regionTabs[activeTab];
    const isInsights = currentTab?.id === 'insights';

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const regionsData = await getRegions();
        setRegions(regionsData.regions || []);

        if (isInsights || !currentTab?.apiName) {
          setLocations([]);
        } else {
          const locationsData = await getLocations({ region: currentTab.apiName });
          setLocations(locationsData.locations || []);
        }
      } catch (err) {
        console.error('Error fetching regions data:', err);
        const status = err.response?.status;
        
        if (status === 401 || status === 403) {
          return;
        }
        
        setError(
          status === 404
            ? 'Feed API not available. Connect the server to see location data.'
            : `Failed to load regions data: ${err.message}`
        );
        setRegions([]);
        setLocations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeTab]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const newRegionId = regionTabs[newValue]?.id;
    if (newRegionId === 'insights') {
      navigate('/feed/regions');
    } else {
      navigate(`/feed/regions/${newRegionId}`);
    }
  };

  const handleLocationClick = (locationId) => {
    navigate(`/feed/locations/${locationId}`);
  };

  const sortedLocations = [...locations].sort((a, b) => {
    if (sortBy === 'storeCode') {
      return (a.storeCode || '').localeCompare(b.storeCode || '');
    }
    return (a.name || '').localeCompare(b.name || '');
  });

  const currentRegion = regionTabs[activeTab];
  const isInsightsTab = currentRegion?.id === 'insights';

  return (
    <FeedLayout
      title="Regions"
      subtitle="View locations by geographic region"
      breadcrumbs={
        regionId && !isInsightsTab
          ? [
              { label: 'Regions', path: '/feed/regions' },
              { label: currentRegion?.name || regionId },
            ]
          : undefined
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Region Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minWidth: 'auto',
              px: 2,
            },
            '& .Mui-selected': {
              color: '#3b5998',
              fontWeight: 600,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#3b5998',
            },
          }}
        >
          {regionTabs.map((tab) => (
            <Tab key={tab.id} label={tab.name} />
          ))}
        </Tabs>
      </Box>

      {isInsightsTab ? (
        <RegionalInsights regions={regions} />
      ) : (
        <>
          {/* Sort Control */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Sort by:
            </Typography>
            <FormControl size="small">
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="storeCode">Store Code</MenuItem>
                <MenuItem value="alphabetical">Alphabetical</MenuItem>
              </Select>
            </FormControl>
            <Chip
              label={`${sortedLocations.length} locations`}
              size="small"
              sx={{ bgcolor: 'rgba(59, 89, 152, 0.1)', color: '#3b5998' }}
            />
          </Box>

          {/* Locations List */}
          {loading ? (
            <Box>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  height={80}
                  sx={{ mb: 2, borderRadius: 2 }}
                />
              ))}
            </Box>
          ) : sortedLocations.length === 0 ? (
            <Card sx={{ border: '1px solid', borderColor: 'divider', p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No locations available for this region. Connect the Feed API to view location data.
              </Typography>
            </Card>
          ) : (
            <List disablePadding>
              {sortedLocations.map((location) => (
                <LocationRow
                  key={location.id}
                  location={location}
                  onClick={() => handleLocationClick(location.id)}
                />
              ))}
            </List>
          )}
        </>
      )}
    </FeedLayout>
  );
};

export default FeedRegions;
